import {
	createContext,
	useState,
	useEffect,
	useContext,
	type PropsWithChildren,
	Dispatch,
	SetStateAction,
} from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { ItemInterface, Item as BaseItem } from '../../util/Item';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from '../../util/Batch';
import { useGridApiRef } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { businessToday } from "../../util/businessClock";

import { addMoney } from "../../util/quantity";
export type BomRole = 'INPUT' | 'OUTPUT';

export class BomItem extends BaseItem {
	role: BomRole;
	unit: string;

	constructor(
		data: ItemInterface & { role?: BomRole; unit?: string } = {}
	) {
		super(data);
		this.role = data.role === 'OUTPUT' ? 'OUTPUT' : 'INPUT';
		this.unit = data.unit ?? '';
	}

	get uniqueId() {
		// role + catalog id + free name + price + unit → distinct lines
		return `${this.role}-${this.id}-${this.item}-${this.price}-${this.unit}`;
	}
}

export interface BomTask extends BatchTask<BomItem> {
	code: string;
	name: string;
	is_active: boolean;
	/** Production depot used to load stocked items for this BOM */
	depot: number;
	/** Server id when editing an existing BOM */
	bom_id?: number | null;
	items: BomItem[];
}

interface ContextProps {
	pendingTasks: BomTask[];
	setPendingTasks: Dispatch<SetStateAction<BomTask[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: BomTask | undefined;
	items: any[];
	setItems: Dispatch<SetStateAction<any[]>>;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

const BomContext = createContext<ContextProps | undefined>(undefined);

export const BomProvider = ({ children }: PropsWithChildren) => {
	const [pendingTasks, setPendingTasks] = useState<BomTask[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);
	const apiRef = useGridApiRef();

	const activeTask = pendingTasks?.find((task) => task.active);

	const addNewTask = () => {
		const newTask: BomTask = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			reference: '',
			receiver: -1,
			code: '',
			name: '',
			is_active: true,
			depot: -1,
			bom_id: null,
			description: '',
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return (this.items || []).reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prev) =>
			prev.map((t) => ({ ...t, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (!fetchedTask) return;

		const itemRecords = (fetchedTask.components || []).map((c: any) => {
			return new BomItem({
				id: c.item,
				item_code: c.item_code || '',
				item: c.item_desc,
				price: c.amount ?? 0,
				quantity: c.quantity || 1,
				role: c.role === 'OUTPUT' ? 'OUTPUT' : 'INPUT',
				unit: c.unit || '',
				isFractional: c.is_fractional === true || c.IsFractional === true || items.find((i: any) => i.id === c.item)?.IsFractional === true,
			});
		});

		updateActiveTask(setPendingTasks, {
			...activeTask,
			id: String(fetchedTask.id),
			bom_id: fetchedTask.id,
			name: fetchedTask.name || '',
			code: fetchedTask.code || '',
			is_active: !!fetchedTask.is_active,
			depot: fetchedTask.depot ?? -1,
			description: fetchedTask.description || '',
			items: itemRecords,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchedTask]);

	return (
		<BomContext.Provider
			value={{
				pendingTasks,
				setPendingTasks,
				fetchedTask,
				setFetchedTask,
				addNewTask,
				activeTask,
				items,
				setItems,
				apiRef,
			}}
		>
			{children}
		</BomContext.Provider>
	);
};

export const useBom = () => {
	const ctx = useContext(BomContext);
	if (!ctx) throw new Error('useBom must be used within BomProvider');
	return ctx;
};
